import React from 'react';
import { FocGapModal, MedicationsIcon, QualityGapModal } from '~/components/Patient';
import {
    cardDescriptionClass,
    cardStatusStyle,
    getSubmissionAnswer,
    formatTitleText,
    enumValue,
    handleButtonKeypress,
} from '~/util';
import { RemindersIcon } from '~/components/Reminders';

interface GapCardProps {
    gap: GapType;
    patientId: number;
    statusFilter: number;
}
export function GapCard({ gap, patientId, statusFilter }: GapCardProps) {
    const [show, setShow] = React.useState(false);
    const [defaultTab, setDefaultTab] = React.useState<null | string>(null);
    const [addReminderOpen, setAddReminderOpen] = React.useState(false);

    function open(
        e: React.MouseEvent<HTMLDivElement, MouseEvent> | React.KeyboardEvent<HTMLDivElement>
    ) {
        if ((e.target as HTMLElement).closest('.reminder-icon')) {
            if (gap?.tasks?.length === 0) {
                setAddReminderOpen(true);
            }
            setDefaultTab('Reminders');
        } else if ((e.target as HTMLElement).closest('.medications-icon')) {
            setDefaultTab('Medications');
        } else {
            setDefaultTab(null);
        }
        setShow(true);
    }

    function close() {
        setShow(false);
        setAddReminderOpen(false);
    }

    const descriptionClass = cardDescriptionClass(gap);
    const submissionDescription = gap.submission_answer
        ? getSubmissionAnswer(gap, gap.submission_answer)
        : gap.description;
    const showNewSubmission =
        (gap.type === enumValue(['gap', 'type', 'Quality Gap']) &&
            statusFilter !== enumValue(['gap', 'status', 'Addressed'])) ||
        (gap.type !== enumValue(['gap', 'type', 'Quality Gap']) &&
            gap.submission_answer?.metadata?.responses &&
            gap.submission_answer.metadata.responses.length > 0);

    function cardDescription() {
        if (
            gap.description &&
            (!showNewSubmission || !gap.submission_answer?.metadata?.responses?.length)
        ) {
            return (
                <div
                    className={`gap-card-description text-center ${
                        descriptionClass ? 'd-flex align-items-center' : ''
                    }`}
                >
                    {descriptionClass ? (
                        <div
                            className={descriptionClass}
                            style={
                                descriptionClass === 'alert'
                                    ? { backgroundColor: gap.metadata?.Color }
                                    : {}
                            }
                            role="alert"
                            title={gap.description}
                        >
                            {gap.description}
                        </div>
                    ) : (
                        gap.description
                    )}
                </div>
            );
        }
        if (showNewSubmission && gap.submission_answer?.metadata?.responses?.length) {
            return (
                <div className="gap-card-description text-center">
                    <div
                        title={formatTitleText(submissionDescription)}
                        className="gap-card-description-text"
                    >
                        <div className="gap-card-description-text-inner">
                            {submissionDescription}
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    }

    return (
        <div
            className={`gap-card col-12 col-md-6 col-lg-4 ${
                gap.type === enumValue(['gap', 'type', 'FOC Gap']) ? 'foc' : 'quality'
            }-gap-card`}
        >
            <div
                className={`card cursor-pointer mb-2 ${cardStatusStyle(gap)}`}
                onClick={open}
                onKeyUp={(e) => handleButtonKeypress(e, open)}
                role="button"
                tabIndex={0}
            >
                <div
                    className={`card-body d-flex justify-content-between align-items-center ${cardStatusStyle(
                        gap
                    )}`}
                >
                    <div className="icons-col foc-icons-col">
                        <MedicationsIcon code={gap.measure?.code || ''} />
                        <RemindersIcon gap={gap} />
                    </div>
                    <div className="gap-card-title-wrapper">
                        <div className="gap-card-name gap-card-title">
                            <b title={gap.measure?.short_name}>{gap.measure?.short_name}</b>
                        </div>
                    </div>
                    {cardDescription()}
                </div>
            </div>
            {enumValue(['gap', 'type', 'FOC Gap']) === gap.type && show && (
                <FocGapModal
                    focGap={gap}
                    addReminderOpen={addReminderOpen}
                    patientId={patientId}
                    handleClose={close}
                    // @ts-expect-error remove after FocGapModal migrates to TS
                    defaultTab={defaultTab}
                />
            )}
            {enumValue(['gap', 'type', 'Quality Gap']) === gap.type && show && (
                <QualityGapModal
                    qualityGap={gap}
                    addReminderOpen={addReminderOpen}
                    patientId={patientId}
                    handleClose={close}
                    statusFilter={statusFilter}
                    // @ts-expect-error remove after QualityGapModal migrates to TS
                    defaultTab={defaultTab}
                />
            )}
        </div>
    );
}
